import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner
} from 'reactstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import api from '../../config';

// Import LightGallery
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';

// Import LightGallery CSS
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';

// Import fallback images
import eventSvg from '../assets/images/event-svg.png';
import logoHere from '../assets/images/Partners/logo-here.png';

const EventDetailPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const lightGalleryTopRef = useRef(null);
  const lightGalleryBottomRef = useRef(null);

  useEffect(() => {
    fetchEventBySlug();
  }, [slug]);

  // Initialize LightGallery when gallery images are loaded
  useEffect(() => {
    let lgTopInstance = null;
    let lgBottomInstance = null;

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      // Initialize top gallery (when no main content)
      if (galleryImages.length > 0 && lightGalleryTopRef.current) {
        lgTopInstance = lightGallery(lightGalleryTopRef.current, {
          plugins: [lgThumbnail, lgZoom],
          speed: 500,
          download: false,
          actualSize: false,
          thumbnail: true,
          animateThumb: false,
          showThumbByDefault: false,
          mode: 'lg-fade',
          getCaptionFromTitleOrAlt: false,
          selector: '.gallery-item'
        });
      }

      // Initialize bottom gallery (when main content exists)
      if (galleryImages.length > 0 && lightGalleryBottomRef.current) {
        lgBottomInstance = lightGallery(lightGalleryBottomRef.current, {
          plugins: [lgThumbnail, lgZoom],
          speed: 500,
          download: false,
          actualSize: false,
          thumbnail: true,
          animateThumb: false,
          showThumbByDefault: false,
          mode: 'lg-fade',
          getCaptionFromTitleOrAlt: false,
          selector: '.gallery-item'
        });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (lgTopInstance) {
        lgTopInstance.destroy();
      }
      if (lgBottomInstance) {
        lgBottomInstance.destroy();
      }
    };
  }, [galleryImages]);

  const fetchEventBySlug = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/get/event-by-slug/${slug}`);

      if (response.data.isOk) {
        setEvent(response.data.data);
        // Fetch gallery images once we have the event data
        if (response.data.data && response.data.data._id) {
          fetchGalleryImages(response.data.data._id);
        }
      } else {
        setError('Event not found');
      }
    } catch (err) {
      console.error('Error fetching event:', err);
      setError(err.response?.data?.message || 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleryImages = async (eventId) => {
    try {
      setGalleryLoading(true);
      const response = await axios.get(`/api/public/get/eventgallery/byevent/${eventId}`);

      if (response.data.isOk && response.data.data) {
        // Flatten all image URLs from all gallery entries for this event
        const allImages = response.data.data.reduce((acc, gallery) => {
          return acc.concat(gallery.imageUrl || []);
        }, []);
        
        // Flatten all video URLs from all gallery entries for this event
        const allVideos = response.data.data.reduce((acc, gallery) => {
          return acc.concat(gallery.videoUrl || []);
        }, []);
        
        setGalleryImages(allImages);
        setGalleryVideos(allVideos.filter(url => url && url.trim() !== ''));
      }
    } catch (err) {
      console.error('Error fetching gallery images:', err);
      console.error('API URL attempted:', `/api/public/get/eventgallery/byevent/${eventId}`);
      // Don't show error for gallery as it's optional
    } finally {
      setGalleryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Helper function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    let videoId = null;
    
    // Handle different YouTube URL formats
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1]?.split('?')[0];
    }
    
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <h3>{error || 'Event not found'}</h3>
          <Button color="primary" href="/events" className="mt-3">
            Back to Events
          </Button>
        </div>
      </Container>
    );
  }

  const eventInfo = [
    {
      icon: 'fas fa-calendar-alt',
      title: 'Date & Time',
      content: formatDate(event.eventDate)
    },
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Location',
      content: `${event.cityId?.cityName || ''}, ${event.countryId?.countryName || ''}`
    },
    {
      icon: 'fas fa-users',
      title: 'Hosted By',
      content: event.host || 'ApexCXOs'
    }
  ];

  const contactInfo = [
    {
      icon: 'fa-solid fa-envelope',
      type: 'email',
      value: event.mail,
      href: `mailto:${event.mail}`
    },
    {
      icon: 'fa-solid fa-phone',
      type: 'phone',
      value: `+ ${event.phone}`,
      href: `tel:${event.phone}`
    }
  ];

  // Helper function to check if there are intermediate sections between main content and gallery
  const hasIntermediateSections = () => {
    return (event.testimonials && event.testimonials.length > 0) || 
           (event.participants && event.participants.length > 0);
  };

  // Helper function to check if main content sections are present
  const hasMainContent = () => {
    return (event.chiefGuest && event.chiefGuest.length > 0) ||
           event.description ||
           (event.agenda && event.agenda.length > 0) ||
           event.buttonLink;
  };

  return (
    <>
      <style>
        {`
          .gallery-item {
            cursor: pointer;
            text-decoration: none !important;
          }
          
          .gallery-image-wrapper {
            overflow: hidden;
            border-radius: 8px;
          }
          
          .gallery-overlay {
            background: rgba(0, 0, 0, 0.5);
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          
          .gallery-item:hover .gallery-overlay {
            opacity: 1 !important;
          }
          
          .gallery-item:hover {
            text-decoration: none !important;
          }
          
          .video-wrapper {
            overflow: hidden;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease;
          }
          
          .video-wrapper:hover {
            transform: translateY(-2px);
          }
          
          .video-wrapper iframe {
            border-radius: 8px;
          }
        `}
      </style>
      <section className="event-details py-3">
        <Container>
          {event.coverImage && event.coverImage.length > 0 && (
          <div className="event-image mb-2">
            <img
              src={`${api.API_URL}/${event.coverImage}`}
              alt={event.title}
              className="#"
              onError={(e) => {
                e.target.src = eventSvg;
              }}
            />
          </div>
          )}

          <Row className="mt-4 g-4">
            <Col lg={hasMainContent() ? 8 : 9}>
              <h2>{event.title}</h2>
              {event.shortDescription && (
                <h5 className="mb-4">{event.shortDescription}</h5>
              )}

              {/* Video Section - Display right after title if NO event details exist */}
              {galleryVideos.length > 0 && !hasMainContent() && (
                <div className="video-section-top mb-4">
                  <h4 className="text-theme fw-bold mb-3">Event Videos</h4>
                  {galleryLoading ? (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading videos...</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {galleryVideos.map((videoUrl, index) => {
                        const embedUrl = getYouTubeEmbedUrl(videoUrl);
                        if (!embedUrl) return null;
                        
                        return (
                          <Col key={index} md={4} className="mb-3">
                            <div className="video-wrapper position-relative">
                              <iframe
                                src={embedUrl}
                                title={`Event Video ${index + 1}`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-100 rounded"
                                style={{ height: '100%' }}
                              ></iframe>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )}

              {/* Gallery Section - Display right after title if NO event details exist - 3 images per row */}
              {galleryImages.length > 0 && !hasMainContent() && (
                <div className="gallery-section-top mb-4">
                  <h4 className="text-theme fw-bold mb-3">Event Gallery</h4>
                  {galleryLoading ? (
                    <div className="text-center">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading gallery...</p>
                    </div>
                  ) : (
                    <Row className="g-3" id="lightgallery-top" ref={lightGalleryTopRef}>
                      {galleryImages.map((imageUrl, index) => {
                        // Construct the full image URL - remove leading slash if exists
                        const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
                        const fullImageUrl = imageUrl.startsWith('http')
                          ? imageUrl
                          : `${api.API_URL}/${cleanImageUrl}`;

                        return (
                          <Col key={index} md={4} className="mb-3">
                            <a
                              className="gallery-item d-block"
                              data-src={fullImageUrl}
                              data-sub-html={`<h4>Event Gallery Image ${index + 1}</h4>`}
                              href={fullImageUrl}
                              onClick={(e) => e.preventDefault()}
                            >
                              <div className="gallery-image-wrapper position-relative">
                                <img
                                  className="img-fluid w-100 rounded"
                                  src={fullImageUrl}
                                  alt={`Event Gallery ${index + 1}`}
                                  loading="lazy"
                                  style={{ height: '220px', objectFit: 'cover' }}
                                  onError={(e) => {
                                    console.error('Image failed to load:', fullImageUrl);
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <div className="gallery-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                  <i className="fas fa-search-plus text-white fs-4"></i>
                                </div>
                              </div>
                            </a>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )}

              {event.chiefGuest && event.chiefGuest.length > 0 && (
                <>
                  <h5 className="text-theme fw-bold mb-3">Chief Guest</h5>
                  {event.chiefGuest.map((guest, index) => (
                    <div key={index} className="host-card w-100 shadow-lg mb-4">
                      {guest.photo && (
                        <img
                          src={`${api.API_URL}/${guest.photo}`}
                          alt={guest.name}
                          className="me-4 host-img"
                          onError={(e) => {
                            e.target.src = eventSvg;
                          }}
                        />
                      )}
                      <div>
                        <h3 className="mb-2 chief-guest-name">{guest.name}</h3>
                        <p className="mb-0 chief-guest-designation">{guest.designation}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {event.description && (
                <div 
                  className={`${!hasMainContent() || (!event.chiefGuest?.length && !event.agenda?.length) ? 'mb-4' : ''}`}
                  dangerouslySetInnerHTML={{ __html: event.description }} 
                />
              )}

              {event.agenda && event.agenda.length > 0 && (
                <>
                  <h5 className="text-theme fw-bold mb-3 mt-4">Event Agenda</h5>
                  <ul className="list-unstyled features-list partner-type agenda-list">
                    {event.agenda.map((item, index) => (
                      <li key={index} className="mb-2">
                        <div className='agenda-item'>{item.agendaTitle}</div>
                        {item.agendaDescription && <p className='agenda-item'>{item.agendaDescription}</p>}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {event.buttonLink && (
                <div className="register-box mb-2 mb-lg-4">
                  <Button
                    href={event.buttonLink}
                    target="_blank"
                    className="btn-custom"
                  >
                    <i className="bi bi-person me-2"></i> Register
                  </Button>
                </div>
              )}

            </Col>

            <Col lg={hasMainContent() ? 4 : 3}>
              <Row>
                <Col xl={12} lg={12}>
                  {event.buttonLink && (
                    <div className="register-box w-100 mb-4 d-lg-block d-none">
                      <Button
                        href={event.buttonLink}
                        target="_blank"
                        className="btn-custom w-100"
                      >
                        <i className="bi bi-person me-2"></i> Register
                      </Button>
                    </div>
                  )}
                </Col>
                <Col xl={12} lg={12} md={12}>
                <Row className='g-1'>
                  {eventInfo.map((info, index) => (
                    <Col xl={12} lg={12} md={6} key={index} className="info-card p-3 mb-4">
                      <h6 className="fw-bold mb-2">
                        <i className={`${info.icon} me-2 text-theme`}></i>{info.title}
                      </h6>
                      <p className="mb-0">{info.content}</p>
                    </Col>
                  ))}

                  <Col xl={12} lg={12}  md={6} className='p-0 mb-4'>
                  <div className="info-card p-3">
                    <h6 className="fw-bold mb-2">
                      <i className="fa-solid fa-address-book me-2 text-theme"></i>Contact:
                    </h6>
                    {contactInfo.map((contact, index) => (
                      <p key={index} className="mb-0">
                        <i className={`${contact.icon} me-2 text-theme`}></i>
                        <a
                          href={contact.href}
                          className="text-decoration-none text-dark"
                        >
                          {contact.value}
                        </a>
                      </p>
                    ))}
                  </div>
                </Col>
                  </Row>
                </Col>
                
                <Col xl={12} lg={12} md={12} sm={12}>
                <Row>
                  {event.sidebarImage && event.sidebarImage.length > 0 && (
                    event.sidebarImage.map((sidebar, sidebarIndex) => (
                      <Col key={sidebarIndex} className="pt-4" xl={12} lg={12} md={6} sm={12}>
                        {sidebar.title && <h5 className="text-theme fw-bold mb-3">{sidebar.title}</h5>}
                        {sidebar.image && sidebar.image.length > 0 && (
                          <Row className="align-items-center">
                            {sidebar.image.map((img, imgIndex) => (
                              <Col key={imgIndex} sm={6} md={6} className="mb-3">
                                <img
                                  src={`${api.API_URL}/${img}`}
                                  alt={`${sidebar.title || 'Image'} ${imgIndex + 1}`}
                                  className="w-100"
                                  onError={(e) => {
                                    e.target.src = logoHere;
                                  }}
                                />
                              </Col>
                            ))}
                          </Row>
                        )}
                      </Col>
                    ))
                  )}
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {event.testimonials && event.testimonials.length > 0 && (
        <section className="testimonials py-3">
          <Container>
            <div className="quote-icon">
              <img src={eventSvg} alt="quote" />
            </div>

            <h2 className="section-title-details mb-3">ApexCXOs - In Their Words</h2>

            <Row className="g-4">
              {event.testimonials.map((testimonial, index) => (
                <Col key={index} lg={12}>
                  <div className="testimonial-card h-100">
                    <div className="testimonial-author d-flex align-items-center my-3">
                      {testimonial.photo && (
                        <img
                          src={`${api.API_URL}/${testimonial.photo}`}
                          className="author-img me-3"
                          alt={testimonial.name}
                          onError={(e) => {
                            e.target.src = eventSvg;
                          }}
                        />
                      )}
                      <div>
                        <h6 className="mb-0">{testimonial.name}</h6>
                        <small>{testimonial.designation}</small>
                      </div>
                    </div>
                    <p className="testimonial-text">{testimonial.message}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Featured Participants Section */}
      {event.participants && event.participants.length > 0 && (
        <section className="featured-participants py-3">
          <Container>
            <h3 className="mb-4">Speakers/ Jury</h3>
            <Row className="g-4">
              {event.participants.map((participant, index) => (
                <Col key={index} lg={4} md={6} sm={12}>
                  <div className="participant-card shadow-sm">
                    {participant.photo && (
                      <img
                        src={`${api.API_URL}/${participant.photo}`}
                        alt={participant.name}
                        className="me-3"
                        onError={(e) => {
                          e.target.src = eventSvg;
                        }}
                      />
                    )}
                    <div>
                      <h6 className="participant-name mb-1">{participant.name}</h6>
                      <p className="mb-0 text-muted">{participant.designation}</p>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Video Section - Display as bottom section when event details exist */}
      {galleryVideos.length > 0 && hasMainContent() && (
        <section className="video-section-full py-4">
          <Container>
            <div className="text-center mb-4">
              <h3 className="section-title mb-3">Event Videos</h3>
            </div>
            {galleryLoading ? (
              <div className="text-center">
                <Spinner color="primary" />
                <p className="mt-2">Loading videos...</p>
              </div>
            ) : (
              <Row className="g-4">
                {galleryVideos.map((videoUrl, index) => {
                  const embedUrl = getYouTubeEmbedUrl(videoUrl);
                  if (!embedUrl) return null;
                  
                  return (
                    <Col key={index} lg={4} md={6} sm={12} className="mb-4">
                      <div className="video-wrapper position-relative">
                        <iframe
                          src={embedUrl}
                          title={`Event Video ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-100 rounded shadow-sm"
                          style={{ height: '100%' }}
                        ></iframe>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Container>
        </section>
      )}

      {/* Gallery Section - Display as bottom-most section when event details exist */}
      {galleryImages.length > 0 && hasMainContent() && (
        <section className="gallery-section-full py-4">
          <Container>
            <div className="text-center mb-4">
              <h3 className="section-title mb-3">Event Gallery</h3>
            </div>
            {galleryLoading ? (
              <div className="text-center">
                <Spinner color="primary" />
                <p className="mt-2">Loading gallery...</p>
              </div>
            ) : (
              <Row className="g-4" id="lightgallery-bottom" ref={lightGalleryBottomRef}>
                {galleryImages.map((imageUrl, index) => {
                  // Construct the full image URL - remove leading slash if exists
                  const cleanImageUrl = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
                  const fullImageUrl = imageUrl.startsWith('http')
                    ? imageUrl
                    : `${api.API_URL}/${cleanImageUrl}`;

                  return (
                    <Col key={index} lg={4} md={6} sm={6} className="mb-4">
                      <a
                        className="gallery-item d-block"
                        data-src={fullImageUrl}
                        data-sub-html={`<h4>Event Gallery Image ${index + 1}</h4>`}
                        href={fullImageUrl}
                        onClick={(e) => e.preventDefault()}
                      >
                        <div className="gallery-image-wrapper position-relative">
                          <img
                            className="img-fluid w-100 rounded shadow-sm"
                            src={fullImageUrl}
                            alt={`Event Gallery ${index + 1}`}
                            loading="lazy"
                            style={{ height: '280px', objectFit: 'cover' }}
                            onError={(e) => {
                              console.error('Image failed to load:', fullImageUrl);
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="gallery-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center opacity-0 transition-opacity">
                            <i className="fas fa-search-plus text-white fs-3"></i>
                          </div>
                        </div>
                      </a>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Container>
        </section>
      )}

    </>
  );
};

export default EventDetailPage;